// IndexMiniChart — kompakt sparkline för marknadsindex.
// Visar index-namn, senaste pris, %-förändring och en 30-dagars mini-graf.
// Klickbart för att navigera till detaljvy.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createChart,
  AreaSeries,
  type IChartApi,
  type ISeriesApi,
  type Time,
} from 'lightweight-charts';
import { fetchHistory, type HistoryRange } from '../../lib/history';
import { useTheme } from '../../context/ThemeContext';
import type { CandlePoint } from '../../types/stock';

interface IndexMiniChartProps {
  ticker: string; // t.ex. '^DJI'
  name: string; // t.ex. 'Dow Jones Industrial Average'
  displayName: string; // t.ex. 'DOW'
  variant?: 'default' | 'wide';
}

export default function IndexMiniChart({
  ticker,
  name,
  displayName,
  variant = 'default',
}: IndexMiniChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Area'> | null>(null);
  const { isDark } = useTheme();
  const [latestPrice, setLatestPrice] = useState<number | null>(null);
  const [change, setChange] = useState<{ abs: number; pct: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;

    fetchHistory(ticker, '1m' as HistoryRange)
      .then((data) => {
        if (cancelled) return;
        if (!data.points || data.points.length === 0) {
          setError('Ingen data');
          return;
        }

        // Beräkna senaste pris och förändring
        const points = data.points;
        const latest = points[points.length - 1];
        const first = points[0];
        setLatestPrice(latest.close);
        const absChange = latest.close - first.close;
        const pctChange = (absChange / first.close) * 100;
        setChange({ abs: absChange, pct: pctChange });

        // Skapa sparkline
        if (chartRef.current) {
          chartRef.current.remove();
        }

        const container = containerRef.current!;
        const bgColor = isDark ? '#0f172a' : '#ffffff';
        const textColor = isDark ? '#94a3b8' : '#475569';
        const lineColor = absChange >= 0
          ? (isDark ? '#10b981' : '#059669') // grön vid uppgång
          : (isDark ? '#ef4444' : '#dc2626'); // röd vid nedgång
        const fillColor = absChange >= 0
          ? (isDark ? 'rgba(16, 185, 129, 0.2)' : 'rgba(5, 150, 105, 0.15)')
          : (isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(220, 38, 38, 0.15)');

        const chart = createChart(container, {
          width: container.clientWidth,
          height: 80,
          layout: {
            background: { color: bgColor },
            textColor,
            fontFamily: 'system-ui, sans-serif',
          },
          grid: {
            vertLines: { visible: false },
            horzLines: { visible: false },
          },
          rightPriceScale: { visible: false },
          timeScale: { visible: false },
          crosshair: { vertLine: { visible: false }, horzLine: { visible: false } },
          handleScale: false,
          handleScroll: false,
        });

        chartRef.current = chart;

        const series = chart.addSeries(AreaSeries, {
          lineColor,
          topColor: fillColor,
          bottomColor: bgColor,
          lineWidth: 2,
        });

        seriesRef.current = series;

        series.setData(
          points.map((p: CandlePoint) => ({
            time: p.time as Time,
            value: p.close,
          })),
        );

        chart.timeScale().fitContent();
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message ?? 'Fel');
      });

    return () => {
      cancelled = true;
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [ticker, isDark]);

  const isUp = change ? change.abs >= 0 : true;
  const colorClass = isUp ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
  const bgClass = isUp
    ? 'bg-emerald-50 dark:bg-emerald-900/20'
    : 'bg-red-50 dark:bg-red-900/20';

  return (
    <Link
      to={`/stocks/${encodeURIComponent(ticker)}`}
      className={`block rounded border border-slate-200 p-4 transition hover:border-indigo-300 hover:shadow-md dark:border-slate-800 dark:hover:border-indigo-700 ${
        variant === 'wide' ? 'bg-white dark:bg-slate-900' : 'bg-white dark:bg-slate-900'
      }`}
    >
      <div className="mb-2 flex items-baseline justify-between">
        <div>
          <h3 className="font-bold text-slate-900 dark:text-white">
            {displayName}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">{name}</p>
        </div>
        {change && (
          <span
            className={`rounded px-2 py-0.5 text-xs font-medium ${bgClass} ${colorClass}`}
          >
            {change.pct >= 0 ? '+' : ''}
            {change.pct.toFixed(2)}%
          </span>
        )}
      </div>

      {error ? (
        <div className="flex h-20 items-center justify-center text-sm text-slate-500 dark:text-slate-400">
          {error}
        </div>
      ) : (
        <>
          <div className="mb-1 text-2xl font-bold tabular-nums text-slate-900 dark:text-white">
            {latestPrice !== null ? latestPrice.toFixed(2) : '—'}
          </div>
          <div ref={containerRef} className="h-20 w-full" />
        </>
      )}
    </Link>
  );
}
